import { useDomTracking } from '@nrdy-marketing-engine/tracking';
import clsx from 'clsx';
import { FormEvent, KeyboardEvent, useEffect, useState } from 'react';
import { InputFieldData, InputStateProps } from '../../@types/input-field-data';
import CheckmarkIcon from '../checkmark';
import InputLabel from './input-label';
import InputValidation from './input-validation';

interface TextInputProps extends InputStateProps, InputFieldData {
  /** Any extra classes to apply to the containing div. */
  className?: string;
  /** Any extra classes to apply to the input control. */
  inputClassName?: string;
  /** Whether the current panel is in a loading state, e.g. waiting for validation. */
  loading?: boolean;
  /** The maximum length of characters allowed. */
  maxLength?: number;
  /** The HTML type of the input field. */
  type: 'text' | 'email' | 'tel' | 'password' | 'number';
}

/** A generic React-powered text field. */
function TextInput({
  name,
  type,
  onInput,
  onNext,
  text,
  required,
  value,
  maxLength,
  inputClassName,
  autoFocus,
  className,
  loading,
  validationErrors,
}: TextInputProps) {
  const blurTrackable = useDomTracking('blur');
  const clickTrackable = useDomTracking('click');
  /* State to check if the value was passed as a prop */
  const [showIconBecauseOfValue, setShowIconBecauseOfValue] = useState(false);

  useEffect(() => {
    /* If the value is set on the first render, 
    it means that I was previously set and validated, 
    then show the checkmark icon. */
    setShowIconBecauseOfValue(Boolean(value));
  }, []);

  function handleChange(event: FormEvent) {
    const target = event.target as HTMLInputElement;
    onInput(target.value);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      onNext();
    }
  }

  /* Show the greenmark icon if
  - the input is loading and there are no validation errors, it means that the input was validated
  - the value was passed as a prop, it means that it was validated before */
  const showIcon = (loading && !validationErrors?.length) || showIconBecauseOfValue;

  return (
    <div className={clsx('flex flex-col md:block', className)}>
      <InputLabel
        className="flex flex-col-reverse"
        name={name}
        ref={clickTrackable}
        required={required}
        text={text}
        type={type}
      >
        <div className="flex flex-row">
          <input
            autoFocus={autoFocus}
            className={clsx(
              'border-2 md:w-[300px] border-gray-300 p-2 rounded-sm outline-none focus-visible:border-primary',
              inputClassName
            )}
            id={`${type}_input_${name || type}`}
            maxLength={maxLength || 100}
            name={name}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            ref={blurTrackable}
            type={type}
            value={(value || '') as string}
          />
          <CheckmarkIcon showIcon={showIcon} />
        </div>
      </InputLabel>
      <InputValidation validationErrors={validationErrors} />
    </div>
  );
}

export default TextInput;
